// packages/shared/src/concerns.ts
//
// The concern taxonomy. A metric is what we measure; a concern is what the
// clinic treats. The recommender matches on concerns, never on metric keys, so
// this file is the joint between the scan and the catalogue.
//
// Concern weights are NOT declared here. They are read from the source metric's
// Skin Score weight, so the two can never drift apart. Concerns that come from
// an anatomy flag carry no weight at all — they gate and they refer, they do
// not rank.

import { METRICS, METRIC_KEYS, ANATOMY_FLAGS, ANATOMY_FLAG_KEYS } from './metrics';
import type { MetricKey, AnatomyFlagKey } from './metrics';

export type ConcernKey =
  // surface
  | 'acne'
  | 'comedones'
  | 'enlarged_pores'
  | 'excess_sebum'
  | 'rough_texture'
  // pigment
  | 'spots'
  | 'uneven_tone'
  // hydration and vitality
  | 'dehydration'
  | 'dullness'
  | 'sensitivity'
  // ageing
  | 'wrinkles'
  | 'laxity'
  // periorbital
  | 'periorbital_darkening'
  | 'tear_trough_hollowing'
  | 'orbital_fat_prolapse'
  // anatomy — gates and referrals, never ranked
  | 'upper_lid_hooding'
  | 'lower_lid_laxity';

export type ConcernCategory =
  | 'surface'
  | 'pigment'
  | 'vitality'
  | 'ageing'
  | 'periorbital'
  | 'anatomy';

export interface ConcernDefinition {
  key: ConcernKey;
  label: string;
  category: ConcernCategory;
  /** The metric or flag this concern is observed through. */
  source: MetricKey | AnatomyFlagKey;
  /** False for anatomy concerns: they never contribute to a treatment's match score. */
  rankable: boolean;
}

export const CONCERNS: Record<ConcernKey, ConcernDefinition> = {
  acne: { key: 'acne', label: 'Acne', category: 'surface', source: 'acne', rankable: true },
  comedones: { key: 'comedones', label: 'Blackheads', category: 'surface', source: 'blackheads', rankable: true },
  enlarged_pores: { key: 'enlarged_pores', label: 'Enlarged pores', category: 'surface', source: 'pores', rankable: true },
  excess_sebum: { key: 'excess_sebum', label: 'Excess oil', category: 'surface', source: 'oiliness', rankable: true },
  rough_texture: { key: 'rough_texture', label: 'Rough texture', category: 'surface', source: 'texture', rankable: true },

  spots: { key: 'spots', label: 'Spots', category: 'pigment', source: 'spots', rankable: true },
  uneven_tone: { key: 'uneven_tone', label: 'Uneven tone', category: 'pigment', source: 'pigmentation', rankable: true },

  dehydration: { key: 'dehydration', label: 'Dehydration', category: 'vitality', source: 'moisture', rankable: true },
  dullness: { key: 'dullness', label: 'Dullness', category: 'vitality', source: 'radiance', rankable: true },
  sensitivity: { key: 'sensitivity', label: 'Redness and sensitivity', category: 'vitality', source: 'redness', rankable: true },

  wrinkles: { key: 'wrinkles', label: 'Lines and wrinkles', category: 'ageing', source: 'wrinkles', rankable: true },
  laxity: { key: 'laxity', label: 'Loss of firmness', category: 'ageing', source: 'firmness', rankable: true },

  periorbital_darkening: { key: 'periorbital_darkening', label: 'Dark circles', category: 'periorbital', source: 'darkCircles', rankable: true },
  tear_trough_hollowing: { key: 'tear_trough_hollowing', label: 'Tear trough hollowing', category: 'periorbital', source: 'tearTrough', rankable: true },
  orbital_fat_prolapse: { key: 'orbital_fat_prolapse', label: 'Eye bags', category: 'periorbital', source: 'eyeBag', rankable: true },

  upper_lid_hooding: { key: 'upper_lid_hooding', label: 'Upper eyelid position', category: 'anatomy', source: 'droopyUpperEyelid', rankable: false },
  lower_lid_laxity: { key: 'lower_lid_laxity', label: 'Lower eyelid position', category: 'anatomy', source: 'droopyLowerEyelid', rankable: false },
};

export const CONCERN_KEYS = Object.keys(CONCERNS) as ConcernKey[];

/** metric key -> concern key. Built from METRICS so it cannot drift. */
export const CONCERN_BY_METRIC = Object.fromEntries(
  METRIC_KEYS.map((k) => [k, METRICS[k].concern as ConcernKey]),
) as Record<MetricKey, ConcernKey>;

/** anatomy flag key -> concern key. */
export const CONCERN_BY_FLAG = Object.fromEntries(
  ANATOMY_FLAG_KEYS.map((k) => [k, ANATOMY_FLAGS[k].concern as ConcernKey]),
) as Record<AnatomyFlagKey, ConcernKey>;

const WEIGHT_BY_CONCERN: Partial<Record<ConcernKey, number>> = Object.fromEntries(
  METRIC_KEYS.map((k) => [METRICS[k].concern, METRICS[k].weight]),
);

/**
 * How much this concern counts when ranking treatments.
 *
 * Read from the source metric's Skin Score weight rather than declared here, so
 * retuning a metric retunes the recommender with it. Anatomy concerns return 0:
 * an eyelid finding must never make a treatment rank higher.
 */
export function concernWeight(key: ConcernKey): number {
  return WEIGHT_BY_CONCERN[key] ?? 0;
}

/**
 * Every metric and flag concern must have a CONCERNS entry, or the recommender
 * silently ignores a finding the report just showed the patient. Called from
 * the test suite rather than at import time — a package this pure should not
 * throw on load.
 */
export function findUnmappedConcerns(): string[] {
  const missing: string[] = [];
  for (const k of METRIC_KEYS) {
    if (!(METRICS[k].concern in CONCERNS)) missing.push(`${k} -> ${METRICS[k].concern}`);
  }
  for (const k of ANATOMY_FLAG_KEYS) {
    if (!(ANATOMY_FLAGS[k].concern in CONCERNS)) missing.push(`${k} -> ${ANATOMY_FLAGS[k].concern}`);
  }
  return missing;
}
